import { Unit, convertUnit, UserPreferredUnit } from '@core';
import type { ActivityReport, CorridorParameterMarginsMap, ParameterNode } from '@data';
import { useTranslate } from '@i18n';
import { typography, GridTable, GridTableRow, CircleIcon, colors } from '@ui';

import * as css from './ParameterGroupTable.css';

type ParameterGroupTableProps = {
    title?: string;
    activityReport?: ActivityReport;
    parameters?: ParameterNode[];
    corridor?: CorridorParameterMarginsMap;
    userPreferredUnit: UserPreferredUnit;
};

export const ParameterGroupTable = ({
    title,
    activityReport,
    parameters,
    corridor,
    userPreferredUnit,
}: ParameterGroupTableProps) => {
    const t = useTranslate();
    return (
        <GridTable
            header={[
                <h3 className={typography({ variant: 'h2', weight: 'normal' })} key="title">
                    {title}
                </h3>,
                <span className={typography({ variant: 'label' })} key="corridor">
                    {t('parameter_group_table.corridor')}
                </span>,
                <span className={typography({ variant: 'label' })} key="avg">
                    {t('parameter_group_table.avg')}
                </span>,
                <span className={typography({ variant: 'label' })} key="std">
                    {t('parameter_group_table.std')}
                </span>,
            ]}
        >
            {parameters?.map((parameter) => {
                const parameterName = parameter.parameter.name;
                const parameterLabel = t(parameter.i18n ?? '-');
                const reportParameter = activityReport?.[parameterName];

                if (!reportParameter || !parameterLabel) {
                    return null;
                }

                const unit = reportParameter.unit as Unit;
                const mean = convertUnit({ unit, value: reportParameter.mean, userPreferredUnit });
                const std = convertUnit({ unit, value: reportParameter.stdDev, userPreferredUnit });
                const parameterMargins = corridor?.[parameterName];

                const isWithinCorridor =
                    mean.value &&
                    parameterMargins &&
                    mean.value >= (parameterMargins.innerMin || 0) &&
                    mean.value <= (parameterMargins.innerMax || 0);

                return (
                    <GridTableRow key={parameter.id}>
                        <span className={typography({ variant: 'h5' })}>{parameterLabel}</span>
                        <span className={css.cell}>
                            {parameterMargins
                                ? `${parameterMargins?.innerMin} to ${parameterMargins?.innerMax}${mean.symbol}`
                                : '-'}
                        </span>
                        <span className={css.cell}>
                            {mean.value ? `${mean.value}${mean.symbol}` : '-'}{' '}
                            <CircleIcon
                                weight="fill"
                                className={css.indicator}
                                color={isWithinCorridor ? colors.green[500] : colors.red[500]}
                            />
                        </span>
                        <span className={css.cell}>{std.value ? `${std.value}${std.symbol}` : '-'}</span>
                    </GridTableRow>
                );
            })}
        </GridTable>
    );
};
